import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Dropdown,
  Checkbox,
  Space,
  Input,
  Tooltip,
  InputNumber,
  Button,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  Camera,
  Edit3,
  Check,
  X,
  AlertTriangle,
  Save,
  ChevronDown,
  ChevronRight,
} from "feather-icons-react/build/IconComponents";
import Shelf from "./Shelf";
import "../styles/rack.css";
import ConfirmationAlert from "./confirm-alert";
import ImageWithBasePath from "../core/img/imagewithbasebath";
import ConfirmationAlertCross from "./confirm-alert-cross";
import shelfHttpService from "../services/http/shelf-management/shelf.service";
import notificationService from "../services/toaster.service";

const Rack = ({
  id,
  number,
  name,
  shelves = [],
  setRacks,
  outlet_id,
  cctvList = [],
  cameras = [],
  onDelete,
  onChange,
  onSaveSuccess, // New prop to notify parent of successful save
  hasUnsavedChanges: rackHasUnsavedChanges = false, // New prop from parent
  // New processor props
  p_number,
  p_ip,
  p_port,
  p_min_weight_change,
}) => {
  // State management
  const [isOpen, setIsOpen] = useState(false);
  const [showCameraMenu, setShowCameraMenu] = useState(false);
  const [selectedCameras, setSelectedCameras] = useState(
    cameras.map((camera) => camera.id || camera)
  );

  // Editing states for different fields
  const [editingField, setEditingField] = useState(null); // 'rackNumber', 'pNumber', 'pIp', 'pPort', 'pMinWeight'
  const [editedValues, setEditedValues] = useState({
    rackNumber: name || "",
    pNumber: p_number || "",
    pIp: p_ip || "",
    pPort: p_port || "",
    pMinWeight: p_min_weight_change || "",
  });

  // Use the hasUnsavedChanges from parent, but also maintain local state for UI
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(
    rackHasUnsavedChanges
  );
  const [validationErrors, setValidationErrors] = useState({});

  // Refs for different input fields
  const rackNumberRef = useRef(null);
  const pNumberRef = useRef(null);
  const pIpRef = useRef(null);
  const pPortRef = useRef(null);
  const pMinWeightRef = useRef(null);

  const { t } = useTranslation();

  // Update local hasUnsavedChanges when prop changes
  useEffect(() => {
    setHasUnsavedChanges(rackHasUnsavedChanges);
  }, [rackHasUnsavedChanges]);

  // Memoized calculations for better performance
  const rackStats = useMemo(() => {
    const totalSensors = shelves.reduce(
      (count, shelf) => count + (shelf.sensors?.length || 0),
      0
    );

    const totalProducts = shelves.reduce((productCount, shelf) => {
      return (
        productCount +
        (shelf.sensors?.reduce(
          (sensorProductCount, sensor) =>
            sensorProductCount +
            (sensor.products?.reduce(
              (sum, product) => sum + (parseInt(product.quantity) || 0),
              0
            ) || 0),
          0
        ) || 0)
      );
    }, 0);

    const totalShelvesWithProducts = shelves.filter((shelf) =>
      shelf.sensors?.some((sensor) =>
        sensor.products?.some((product) => (product.quantity || 0) > 0)
      )
    ).length;

    return {
      shelves: shelves.length,
      sensors: totalSensors,
      products: totalProducts,
      activeShelvesCount: totalShelvesWithProducts,
    };
  }, [shelves]);

  // Memoized filtered cameras for performance
  const availableCameras = useMemo(() => {
    return cctvList.filter((camera) => camera && camera.id && camera.title);
  }, [cctvList]);

  const assignedCameraDetails = useMemo(() => {
    return availableCameras.filter((camera) =>
      selectedCameras.includes(camera.id)
    );
  }, [availableCameras, selectedCameras]);

  // Initialize selected cameras when cameras prop changes
  useEffect(() => {
    const cameraIds = cameras
      .map((camera) => (typeof camera === "object" ? camera.id : camera))
      .filter(Boolean);
    setSelectedCameras(cameraIds);
  }, [cameras]);

  // Update edited values when props change
  useEffect(() => {
    setEditedValues({
      rackNumber: name || "",
      pNumber: p_number || "",
      pIp: p_ip || "",
      pPort: p_port || "",
      pMinWeight: p_min_weight_change || "",
    });
  }, [name, p_number, p_ip, p_port, p_min_weight_change]);

  // Handle clicks outside camera dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCameraMenu && !event.target.closest(".camera-dropdown")) {
        setShowCameraMenu(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showCameraMenu]);

  // Focus input when editing starts
  useEffect(() => {
    const refMap = {
      rackNumber: rackNumberRef,
      pNumber: pNumberRef,
      pIp: pIpRef,
      pPort: pPortRef,
      pMinWeight: pMinWeightRef,
    };

    if (editingField && refMap[editingField]?.current) {
      setTimeout(() => {
        refMap[editingField].current.focus();
        if (refMap[editingField].current.select) {
          refMap[editingField].current.select();
        }
      }, 100);
    }
  }, [editingField]);

  // Validation functions
  const validateRackNumber = useCallback(
    (value, excludeCurrentRack = false) => {
      if (!value || !value.toString().trim()) {
        return "Rack number is required";
      }

      const numValue = parseInt(value);
      if (isNaN(numValue) || numValue < 1) {
        return "Rack number must be a positive number";
      }

      return null;
    },
    []
  );

  const validateIpAddress = useCallback((value) => {
    if (!value) return null;

    const ipRegex =
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::1$|^localhost$/;
    if (!ipRegex.test(value)) {
      return "Please enter a valid IP address";
    }
    return null;
  }, []);

  const validatePort = useCallback((value) => {
    if (!value) return null;

    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 1 || numValue > 65535) {
      return "Port must be between 1 and 65535";
    }
    return null;
  }, []);

  const validateMinWeight = useCallback((value) => {
    if (!value) return null;

    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) {
      return "Minimum weight change must be a positive number";
    }
    return null;
  }, []);

  // Handle camera icon click
  const handleCameraIconClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowCameraMenu((prev) => !prev);
  }, []);

  // Handle camera selection
  const handleCameraSelection = useCallback(
    (cameraId, e) => {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }

      if (!cameraId) return;

      setSelectedCameras((prev) => {
        const isChecked = !prev.includes(cameraId);
        console.log("Is Checked: ", isChecked);
        const newSelection = !isChecked
          ? prev.filter((id) => id !== cameraId)
          : [...prev, cameraId];

        setRacks((prevRacks) => {
          return prevRacks.map((rack) =>
            rack.id === id
              ? {
                  ...rack,
                  shelves: rack.shelves.map((shelf) => {
                    return {
                      ...shelf,
                      cameras_location: isChecked
                        ? {
                            ...shelf.cameras_location,
                            [cameraId]: "(0,0)",
                          }
                        : Object.fromEntries(
                            Object.entries(shelf.cameras_location || {}).filter(
                              ([key]) => key != cameraId
                            )
                          ),
                    };
                  }),
                  cameras: newSelection,
                }
              : rack
          );
        });

        onChange?.();

        return newSelection;
      });
    },
    [id, setRacks, onChange]
  );

  // Toggle dropdown
  const toggleDropdown = useCallback((e) => {
    // Don't toggle if clicking on action buttons
    if (
      e.target.closest(".action-buttons") ||
      e.target.closest(".camera-dropdown") ||
      e.target.closest("button") ||
      e.target.closest(".ant-input") ||
      e.target.closest(".ant-input-number")
    ) {
      return;
    }
    setIsOpen((prev) => !prev);
  }, []);

  // Start editing field
  const startEditing = useCallback((fieldName, e) => {
    e.stopPropagation();
    setEditingField(fieldName);
    setValidationErrors({});
  }, []);

  // Handle input change
  const handleInputChange = useCallback(
    (fieldName, value) => {
      setEditedValues((prev) => ({
        ...prev,
        [fieldName]: value,
      }));

      if (validationErrors[fieldName]) {
        setValidationErrors((prev) => ({
          ...prev,
          [fieldName]: null,
        }));
      }
    },
    [validationErrors]
  );

  // Save field value
  const saveField = useCallback(
    (fieldName) => {
      const value = editedValues[fieldName];
      let validationError = null;

      switch (fieldName) {
        case "rackNumber":
          validationError = validateRackNumber(value);
          break;
        case "pIp":
          validationError = validateIpAddress(value);
          break;
        case "pPort":
          validationError = validatePort(value);
          break;
        case "pMinWeight":
          validationError = validateMinWeight(value);
          break;
        default:
          break;
      }

      if (validationError) {
        setValidationErrors((prev) => ({
          ...prev,
          [fieldName]: validationError,
        }));
        return;
      }

      const fieldMapping = {
        rackNumber: "name",
        pNumber: "p_number",
        pIp: "p_ip",
        pPort: "p_port",
        pMinWeight: "p_min_weight_change",
      };

      const actualFieldName = fieldMapping[fieldName];
      const processedValue =
        fieldName === "rackNumber" ? `Rack ${parseInt(value) || 1}` : value;

      setRacks((prev) =>
        prev.map((rack) =>
          rack.id === id ? { ...rack, [actualFieldName]: processedValue } : rack
        )
      );

      onChange?.();
      setEditingField(null);
    },
    [
      editedValues,
      validateRackNumber,
      validateIpAddress,
      validatePort,
      validateMinWeight,
      id,
      setRacks,
      onChange,
    ]
  );

  const normalizeCameras = useCallback((cameras) => {
    if (!Array.isArray(cameras)) return [];
    return cameras
      .map((camera) =>
        typeof camera === "object" && camera?.id ? camera.id : camera
      )
      .filter(Boolean);
  }, []);

  const cleanTempId = useCallback((id, tempPrefix) => {
    if (!id) return null;
    return id.toString().includes(tempPrefix) ? null : id;
  }, []);

  // Save all changes - Individual rack save
  const saveAllChanges = useCallback(
    async (e) => {
      e.stopPropagation();

      let selectedRack;
      setRacks((prevItems) => {
        selectedRack = prevItems.find((item) => item.id === id);
        return prevItems;
      });
      console.log("Saving individual rack changes...", selectedRack);

      try {
        const response = await shelfHttpService.saveIndividualRack(
          {
            ...selectedRack,
            id: cleanTempId(selectedRack.id, "temp_rack_"),
            outlet_id: outlet_id,
            cameras: normalizeCameras(selectedRack.cameras),
            shelves: (selectedRack.shelves || []).map((shelf) => ({
              id: cleanTempId(shelf.id, "temp_shelf_"),
              name: shelf.name?.trim() || "",
              cameras_location: shelf.cameras_location || {},
              sensors: (shelf.sensors || []).map((sensor) => ({
                id: cleanTempId(sensor.id, "temp_sensor_"),
                sensor_number: sensor.sensor_number,
                products: (sensor.products || []).map((product) => ({
                  product_id: product.id,
                  quantity: Math.max(0, parseInt(product.quantity) || 0),
                })),
              })),
            })),
          },
          true
        );

        if (response?.status === 200) {
          notificationService.successMessage(
            t("shelf.dataSavedSuccessfully") || "Rack saved successfully!"
          );

          // Notify parent that this rack has been saved successfully
          onSaveSuccess?.();
        } else {
          throw new Error(`Server returned status ${response?.status}`);
        }
      } catch (error) {
        console.error("Error saving individual rack:", error);
        notificationService.errorMessage(
          "Failed to save rack. Please try again."
        );
      }
    },
    [id, outlet_id, setRacks, cleanTempId, normalizeCameras, onSaveSuccess, t]
  );

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setEditedValues({
      rackNumber: name || "",
      pNumber: p_number || "",
      pIp: p_ip || "",
      pPort: p_port || "",
      pMinWeight: p_min_weight_change || "",
    });
    setEditingField(null);
    setValidationErrors({});
  }, [name, p_number, p_ip, p_port, p_min_weight_change]);

  // Handle key press for editing
  const handleKeyPress = useCallback(
    (fieldName, e) => {
      if (e.key === "Enter") {
        saveField(fieldName);
      } else if (e.key === "Escape") {
        cancelEditing();
      }
    },
    [saveField, cancelEditing]
  );

  // Add shelf with smart naming
  const addShelf = useCallback(() => {
    setRacks((prev) =>
      prev.map((rack) => {
        if (rack.id !== id) return rack;

        const existingShelfNumbers =
          rack.shelves
            ?.map((shelf) => {
              const match = shelf.name?.match(/^Shelf (\d+)$/);
              return match ? parseInt(match[1]) : null;
            })
            .filter((num) => num !== null) || [];

        let nextShelfNumber = 1;
        while (existingShelfNumbers.includes(nextShelfNumber)) {
          nextShelfNumber++;
        }

        const newShelf = {
          id: `temp_shelf_${Date.now()}_${Math.random()
            .toString(36)
            .substr(2, 9)}`,
          name: `Shelf ${nextShelfNumber}`,
          sensors: [],
          cameras_location: {},
        };

        return {
          ...rack,
          shelves: [...rack.shelves, newShelf],
        };
      })
    );

    onChange?.();
  }, [id, setRacks, onChange]);

  // Delete rack
  const handleDeleteRack = useCallback(() => {
    if (onDelete) {
      onDelete(id);
    } else {
      setRacks((prev) => prev.filter((rack) => rack.id !== id));
    }
    onChange?.();
  }, [id, onDelete, setRacks, onChange]);

  // Handle changes in shelves - notify parent of changes
  const onChangeShelves = useCallback(() => {
    onChange?.();
    console.log("Shelves Changes ##################");
  }, [onChange]);

  // Delete shelf
  const deleteShelf = useCallback(
    (shelfId) => {
      setRacks((prev) =>
        prev.map((rack) =>
          rack.id === id
            ? {
                ...rack,
                shelves: rack.shelves.filter((shelf) => shelf.id !== shelfId),
              }
            : rack
        )
      );
      onChange?.();
    },
    [id, setRacks, onChange]
  );

  // Get rack number from name
  const getRackNumber = useCallback(() => {
    const match = name?.match(/^Rack (\d+)$/);
    return match ? match[1] : "";
  }, [name]);

  // Render editable field horizontally
  const renderEditableField = useCallback(
    (
      fieldName,
      label,
      value,
      placeholder = "",
      inputType = "text",
      width = "80px"
    ) => {
      const isEditing = editingField === fieldName;
      const hasError = validationErrors[fieldName];
      const hasValue = value && value.toString().trim();

      return (
        <div
          className="d-flex flex-column"
          style={{
            minWidth: width,
            position: "relative",
            margin: "0 2px",
          }}
        >
          {/* Field Label */}
          <div
            className="field-label"
            style={{
              fontSize: "9px",
              fontWeight: "600",
              color: "#9595b5",
              textTransform: "uppercase",
              letterSpacing: "0.3px",
              marginBottom: "3px",
              textAlign: "center",
              lineHeight: "1",
            }}
          >
            {label}
          </div>

          {isEditing ? (
            /* Editing Mode */
            <div className="editing-container">
              <div
                className="input-wrapper"
                style={{
                  position: "relative",
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                  border: hasError ? "1px solid #dc3545" : "1px solid #007bff",
                  borderRadius: "6px",
                  padding: "1px",
                  boxShadow: hasError
                    ? "0 0 0 2px rgba(220, 53, 69, 0.1)"
                    : "0 0 0 2px rgba(0, 123, 255, 0.1)",
                  transition: "all 0.2s ease",
                }}
              >
                {inputType === "number" ? (
                  <InputNumber
                    ref={
                      fieldName === "rackNumber"
                        ? rackNumberRef
                        : fieldName === "pPort"
                        ? pPortRef
                        : fieldName === "pMinWeight"
                        ? pMinWeightRef
                        : null
                    }
                    value={editedValues[fieldName]}
                    onChange={(val) => handleInputChange(fieldName, val)}
                    onKeyDown={(e) => handleKeyPress(fieldName, e)}
                    style={{
                      fontSize: "10px",
                      fontWeight: "500",
                      border: "none",
                      boxShadow: "none",
                      backgroundColor: "transparent",
                      width: width,
                    }}
                    placeholder={placeholder}
                    min={
                      fieldName === "rackNumber"
                        ? 1
                        : fieldName === "pPort"
                        ? 1
                        : 0
                    }
                    max={fieldName === "pPort" ? 65535 : undefined}
                    controls={false}
                  />
                ) : (
                  <Input
                    size="small"
                    ref={
                      fieldName === "pNumber"
                        ? pNumberRef
                        : fieldName === "pIp"
                        ? pIpRef
                        : null
                    }
                    value={editedValues[fieldName]}
                    onChange={(e) =>
                      handleInputChange(fieldName, e.target.value)
                    }
                    onKeyDown={(e) => handleKeyPress(fieldName, e)}
                    style={{
                      fontSize: "10px",
                      fontWeight: "500",
                      border: "none",
                      boxShadow: "none",
                      backgroundColor: "transparent",
                      width: width,
                    }}
                    placeholder={placeholder}
                    maxLength={
                      fieldName === "pNumber"
                        ? 100
                        : fieldName === "pIp"
                        ? 45
                        : undefined
                    }
                  />
                )}
              </div>

              {/* Error Message */}
              {hasError && (
                <div
                  className="error-message"
                  style={{
                    fontSize: "8px",
                    color: "#dc3545",
                    textAlign: "center",
                    marginTop: "2px",
                    fontWeight: "500",
                    background: "rgba(220, 53, 69, 0.1)",
                    padding: "1px 4px",
                    borderRadius: "3px",
                    border: "1px solid rgba(220, 53, 69, 0.2)",
                    lineHeight: "1.2",
                  }}
                >
                  {hasError}
                </div>
              )}
            </div>
          ) : (
            /* Display Mode */
            <div
              className="display-container"
              onClick={(e) => startEditing(fieldName, e)}
              style={{
                position: "relative",
                background: hasValue
                  ? "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)"
                  : "linear-gradient(135deg, #ffffff 0%, #f1f3f4 100%)",
                border: hasValue ? "1px solid #ced4da" : "1px dashed #dee2e6",
                borderRadius: "5px",
                padding: "5px 8px",
                minHeight: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                if (hasValue) {
                  e.target.style.borderColor = "#28a745";
                  e.target.style.transform = "translateY(-0.5px)";
                  e.target.style.boxShadow =
                    "0 2px 6px rgba(40, 167, 69, 0.15)";
                } else {
                  e.target.style.borderColor = "#007bff";
                  e.target.style.background =
                    "linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)";
                  e.target.style.transform = "translateY(-0.5px)";
                  e.target.style.boxShadow =
                    "0 2px 6px rgba(0, 123, 255, 0.15)";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = hasValue ? "#ced4da" : "#dee2e6";
                e.target.style.background = hasValue
                  ? "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)"
                  : "linear-gradient(135deg, #ffffff 0%, #f1f3f4 100%)";
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }}
            >
              {/* Status Indicator */}
              <div
                className="status-indicator"
                style={{
                  position: "absolute",
                  top: "2px",
                  right: "2px",
                  width: "4px",
                  height: "4px",
                  borderRadius: "50%",
                  backgroundColor: hasValue ? "#28a745" : "#ffc107",
                }}
              />

              {/* Value Display */}
              <div
                className="value-display"
                style={{
                  fontSize: "10px",
                  fontWeight: hasValue ? "600" : "400",
                  color: hasValue ? "#2d3436" : "#74b9ff",
                  fontStyle: hasValue ? "normal" : "italic",
                  textAlign: "center",
                  lineHeight: "1.1",
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {hasValue ? value : ``}
              </div>
            </div>
          )}
        </div>
      );
    },
    [
      editingField,
      editedValues,
      validationErrors,
      handleInputChange,
      handleKeyPress,
      saveField,
      cancelEditing,
      startEditing,
    ]
  );

  // Camera menu content
  const cameraMenuContent = useMemo(
    () => (
      <div className="camera-menu-content">
        <div className="p-3" style={{ maxWidth: "300px" }}>
          <div className="mb-2">
            <strong>Select Cameras for {name}</strong>
            <small className="d-block text-muted">
              {selectedCameras.length} of {availableCameras.length} selected
            </small>
          </div>

          {availableCameras.length === 0 ? (
            <div className="text-muted text-center py-3">
              <Camera size={24} className="mb-2" />
              <div>No cameras available</div>
            </div>
          ) : (
            <Space direction="vertical" style={{ width: "100%" }}>
              {availableCameras.map((camera) => (
                <div key={camera.id} className="w-100 ">
                  <Checkbox
                    checked={selectedCameras.includes(camera.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleCameraSelection(camera.id, e);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="d-flex align-items-center p-0 m-0"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{camera.title}</span>
                      {camera.location && (
                        <small className="text-muted">
                          ({camera.location})
                        </small>
                      )}
                    </div>
                  </Checkbox>
                </div>
              ))}
            </Space>
          )}

          {selectedCameras.length > 0 && (
            <div className="mt-3 pt-2 border-top">
              <small className="text-success">
                ✓ {selectedCameras.length} camera(s) assigned to this rack
              </small>
            </div>
          )}
        </div>
      </div>
    ),
    [name, selectedCameras, availableCameras, handleCameraSelection]
  );

  const showCameraWarning =
    availableCameras.length > 0 && selectedCameras.length === 0;

  return (
    <div className="rack-container-full mb-2">
      {/* Main Rack Header - Sleek and Compact */}
      <div
        className="rack-header cursor-pointer d-flex justify-content-between"
        onClick={toggleDropdown}
        style={{
          backgroundColor: "#ffffff",
          border: "1px solid #e1e5e9",
          borderRadius: "12px",
          padding: "8px 12px", // Reduced padding
          transition: "all 0.2s ease",
          minHeight: "48px", // Reduced height
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Left side - Rack Name and Stats */}
        <div className="d-flex align-items-center ">
          <div className="me-3" style={{ minWidth: "215px" }}>
            <h6
              className="mb-0 d-flex align-items-center"
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#2c3e50",
                lineHeight: "1.2",
              }}
            >
              Rack {getRackNumber()}
              {hasUnsavedChanges && (
                <span
                  className="ms-2"
                  style={{
                    backgroundColor: "#ffc107",
                    color: "#212529",
                    padding: "1px 4px",
                    borderRadius: "6px",
                    fontSize: "8px",
                    fontWeight: "700",
                    marginTop: "-10px",
                  }}
                  title="Unsaved changes"
                >
                  *
                </span>
              )}
            </h6>
            <div
              className="text-muted"
              style={{ fontSize: "10px", lineHeight: "1.1" }}
            >
              <span className="me-2">
                <strong>{rackStats.shelves}</strong> {t("shelf.shalves")}
              </span>
              <span className="me-2">
                <strong>{rackStats.sensors}</strong> {t("shelf.sensors")}
              </span>
              <span className="me-2">
                <strong>{rackStats.products}</strong> {t("shelf.product")}
              </span>
              {rackStats.activeShelvesCount > 0 && (
                <span className="text-success">
                  ({rackStats.activeShelvesCount} active)
                </span>
              )}
            </div>
          </div>
          <div style={{ borderLeft: "1px solid #ccc", height: "40px" }}></div>
          <div
            className="d-flex justify-content-center align-items-center gap-2 mx-3"
            style={{ flexShrink: 0 }}
          >
            {renderEditableField(
              "rackNumber",
              "Rack #",
              getRackNumber(),
              "Enter number",
              "number",
              "70px"
            )}
            {renderEditableField(
              "pNumber",
              "Processor #",
              p_number,
              "Enter processor",
              "text",
              "90px"
            )}
            {renderEditableField(
              "pIp",
              "IP Address",
              p_ip,
              "Enter IP",
              "text",
              "110px"
            )}
            {renderEditableField(
              "pPort",
              "Port",
              p_port,
              "Enter port",
              "number",
              "60px"
            )}
            {renderEditableField(
              "pMinWeight",
              "Min Weight (G)",
              p_min_weight_change,
              "Enter weight",
              "number",
              "80px"
            )}
          </div>
        </div>

        {/* Right side - Action buttons - More Compact */}
        <div
          className="d-flex align-items-center action-buttons"
          style={{ flexShrink: 0, gap: "6px" }}
        >
          {assignedCameraDetails.length > 0 && (
            <div>
              <small className="text-muted" style={{ fontSize: "10px" }}>
                {assignedCameraDetails.map((camera) => camera.title).join(", ")}
              </small>
            </div>
          )}

          <div>
            <Dropdown
              overlay={cameraMenuContent}
              trigger={["click"]}
              open={showCameraMenu}
              onOpenChange={(visible) => {
                setShowCameraMenu(visible);
              }}
              overlayClassName="camera-dropdown"
              placement="bottomRight"
              getPopupContainer={() => document.body}
            >
              <Tooltip
                title={
                  selectedCameras.length > 0
                    ? `${selectedCameras.length} camera(s) assigned`
                    : "Assign cameras to this rack"
                }
              >
                <div
                  className={`camera-icon-wrapper position-relative ${
                    showCameraWarning ? "warning" : ""
                  }`}
                  onClick={handleCameraIconClick}
                  style={{
                    cursor: "pointer",
                    padding: "4px",
                    border: "1px solid #dee2e6",
                    borderRadius: "6px",
                    backgroundColor: "white",
                    transition: "all 0.2s ease",
                    minWidth: "28px",
                    minHeight: "28px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = "#007bff";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = "#dee2e6";
                  }}
                >
                  <ImageWithBasePath
                    src="assets/img/icons/cctv-camera-icon.png"
                    alt="img"
                    height={"15px"}
                  />
                  {selectedCameras.length > 0 && (
                    <span
                      className="camera-count"
                      style={{
                        position: "absolute",
                        top: "-3px",
                        right: "-3px",
                        backgroundColor: "#dc3545",
                        color: "white",
                        borderRadius: "50%",
                        width: "14px",
                        height: "14px",
                        fontSize: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "600",
                      }}
                    >
                      {selectedCameras.length}
                    </span>
                  )}
                  {showCameraWarning && (
                    <AlertTriangle size={8} className="warning-icon" />
                  )}
                </div>
              </Tooltip>
            </Dropdown>
          </div>

          {/* Individual Save Button - Only show if rack has unsaved changes */}
          {hasUnsavedChanges && (
            <button
              onClick={saveAllChanges}
              title="Save this rack"
              style={{
                border: "1px solid #28a745",
                backgroundColor: "#FFF",
                color: "#28a745",
                borderRadius: "6px",
                padding: "4px 8px",
                display: "flex",
                alignItems: "center",
                gap: "3px",
                fontSize: "10px",
                fontWeight: "500",
                minHeight: "28px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#28a745";
                e.target.style.color = "white";
                e.target.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#FFF";
                e.target.style.color = "#28a745";
                e.target.style.transform = "translateY(0)";
              }}
            >
              <Save size={10} />
              Save
            </button>
          )}

          {/* Delete Rack - Smaller */}
          <div
            style={{ minHeight: "28px", display: "flex", alignItems: "center" }}
          >
            <ConfirmationAlertCross
              title={`Delete ${name}?`}
              text={`This will permanently delete "${name}" and all its ${rackStats.shelves} shelves with ${rackStats.sensors} sensors. This action cannot be undone.`}
              onConfirm={handleDeleteRack}
              confirmText="Delete Rack"
              cancelText="Cancel"
            />
          </div>
        </div>
      </div>

      {/* Rack Details (Shelves) - Same as before */}
      {isOpen && (
        <div
          className="rack-details"
          style={{
            backgroundColor: "#fafbfc",
            border: "1px solid #e1e5e9",
            borderTop: "none",
            borderRadius: "0 0 12px 12px",
            padding: "12px 16px",
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          {/* Shelves List */}
          {shelves.length === 0 ? (
            <div className="text-center py-3 text-muted">
              <div className="mb-2" style={{ fontSize: "14px" }}>
                No shelves in this rack
              </div>
              <small style={{ fontSize: "11px" }}>
                Add your first shelf to start organizing products
              </small>
            </div>
          ) : (
            <div style={{ maxHeight: "280px", overflowY: "auto" }}>
              {shelves.map((shelf, index) => (
                <Shelf
                  key={shelf.id || index}
                  id={shelf.id}
                  rackId={id}
                  rackName={name}
                  name={shelf.name}
                  sensors={shelf.sensors || []}
                  cameras_location={shelf.cameras_location || {}}
                  setRacks={setRacks}
                  cameras={assignedCameraDetails}
                  onDelete={() => deleteShelf(shelf.id)}
                  onChange={onChangeShelves}
                />
              ))}
            </div>
          )}

          {/* Add Shelf Button */}
          <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
            <Button
              onClick={addShelf}
              disabled={!name.trim()}
              size="small"
              type="primary"
              style={{
                backgroundColor: "#333",
                borderColor: "#333",
                borderRadius: "4px",
                fontSize: "12px",
                height: "28px",
                padding: "0 12px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <span style={{ fontSize: "14px" }}>+</span>
              {t("shelf.addShelf")}
            </Button>

            {shelves.length > 0 && (
              <small className="text-muted" style={{ fontSize: "10px" }}>
                {shelves.length} shelf{shelves.length !== 1 ? "es" : ""} in this
                rack
              </small>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Rack;
